// Company upload-first (kyc-sent): fills the form from the reading the client confirmed ("Use these details").
//
// - Only empty fields are filled; nothing the client or staff entered is overwritten (the company name included).
// - Signatories: one row per kept director / manager / signatory (empty rows filled first, no duplicate names).
// - Shareholders: only when the ownership is still empty. A single shareholder without a % holds 100%; when one
//   % is missing, it is the rest up to 100%.
//   Only individuals: desktop gets a "direct" structure with manual entry; mobile prepares the ownership
//   assistant's answers and the Ownership step generates its result without showing the chat
//   (documentPrefillOwnership "generate").
//   A company among them: the direct shareholders (companies first) prepare the structure, and the client
//   continues with the owners behind those companies. Mobile: the assistant's chat opens already filled
//   (documentPrefillOwnership "chat"). Desktop: the visual builder and the guided assistant start with them
//   (documentShareholders), the type of each company asked first.
// - customer.documentPrefill keeps what was filled, so the form can mark it ("From your documents") while the
//   value is unchanged.

import { identityConflicts, linkSignatory, sameNamePeople } from "./linkedScreeningPerson";

const clean = (value) => String(value ?? "").trim();
const nameKey = (value) => clean(value).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
  .split(/[^a-z0-9]+/).filter(Boolean).sort().join(" ");
// Dates as the form keeps them (a Date at local midnight, as the ID-document pre-fill), compared as "YYYY-MM-DD".
const toFormDate = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`) : null);
function isoDate(value) {
  const date = value instanceof Date ? value : typeof value?.toDate === "function" ? value.toDate() : value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
let rowCounter = 0;
const rowId = (prefix) => `${prefix}_doc_${Date.now().toString(36)}_${(rowCounter += 1).toString(36)}`;

// Directors -> "Director"; managers, legal representatives, officers and signatories -> "Authorised signatory".
export function signatoryCapacity(roles = []) {
  return roles.includes("director") ? "Director" : "Authorised signatory";
}

const CUSTOMER_FIELDS = [
  ["name", (reading) => reading.company?.name],
  ["address", (reading) => reading.company?.address],
  ["city", (reading) => reading.company?.city],
  ["dobOrIncorporationDate", (reading) => reading.company?.incorporationDate],
];

// The printed %, with the missing one completed when it is the only one missing (a single shareholder: 100%).
function completedPercents(people) {
  const known = people.map((person) => (Number.isFinite(person.sharePercent) ? person.sharePercent : null));
  if (known.filter((value) => value === null).length !== 1) return known;
  const rest = Math.round((100 - known.reduce((sum, value) => sum + (value || 0), 0)) * 100) / 100;
  return rest > 0 ? known.map((value) => (value === null ? rest : value)) : known;
}
// The mobile assistant keeps a % as "NN.NN" ("100.00" for 100).
function assistantPercent(value) {
  if (!Number.isFinite(value) || value <= 0 || value > 100) return "";
  if (value === 100) return "100.00";
  const cents = Math.round(value * 100);
  return `${String(Math.floor(cents / 100)).padStart(2, "0")}.${String(cents % 100).padStart(2, "0")}`;
}

// Why the owners of company shareholders are asked (mobile chat, desktop visual builder), e.g. "Your documents
// show that PSG A LLC has 3 direct shareholders, which include the company Coney Glen Holdings Limited. …".
export function companyShareholdersNotice(names = [], entityName = "", directCount = 0) {
  const list = names.filter(Boolean);
  if (!list.length) return "";
  const several = list.length > 1;
  const count = Math.max(Number(directCount) || 0, list.length);
  const companies = several
    ? `the companies ${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}` : `the company ${list[0]}`;
  const which = count === list.length ? (count === 1 ? "which is" : "which are") : "which include";
  // One company is named again (no doubt about what "it" means); several are "these companies".
  const owners = several ? "these companies, down to the individuals behind them" : `${list[0]}, down to the individuals behind it`;
  return `Your documents show that ${entityName} has ${count === 1 ? "one direct shareholder" : `${count} direct shareholders`}, ${which} ${companies}. ` +
    `Under anti-money laundering regulations, we need to identify who owns or controls ${owners}.`;
}

// A signatory filled from the documents who is also a shareholder / controlling person with the same details
// is the same person: linked directly (as "Yes, use this person"), no question asked. Any difference, or
// several people with that name, leaves the usual question.
export function linkPrefilledSignatories(ownership, prefilledSignatories = {}) {
  let linked = ownership;
  (linked.signatories || []).forEach((row, index) => {
    if (!prefilledSignatories[row?._rowId]) return;
    const current = linked.signatories[index];
    // The same row kept in two lists (controlling persons: ubos and controllingPersonsNatural) is one person.
    const matches = sameNamePeople(linked, current)
      .filter((person, i, all) => all.findIndex((other) => (other.row?._rowId || other.ref) === (person.row?._rowId || person.ref)) === i);
    if (matches.length === 1 && identityConflicts(current, matches[0].row).length === 0) {
      linked = linkSignatory(linked, index, matches[0]);
    }
  });
  return linked;
}

/**
 * The form with the confirmed reading applied. mobile: the shareholders go to the ownership assistant (see above).
 */
export function applyCompanyReading(form, reading, { mobile = false } = {}) {
  if (!reading) return form;
  const customer = { ...(form.customer || {}), companyDocumentsReading: reading };
  const ownership = { ...(form.ownership || {}) };
  const prefill = { signatories: {}, shareholders: {} };

  // Customer identification: empty fields only.
  for (const [field, read] of CUSTOMER_FIELDS) {
    const value = clean(read(reading));
    if (field === "dobOrIncorporationDate") {
      const date = toFormDate(value);
      if (date && !customer[field]) { customer[field] = date; prefill[field] = value; }
      continue;
    }
    if (value && !clean(customer[field])) {
      customer[field] = value;
      prefill[field] = value;
    }
  }

  // Signatories: empty rows first, then new rows; a name already listed is not added again.
  const signatories = Array.isArray(ownership.signatories) ? ownership.signatories.map((row) => ({ ...row })) : [];
  const listed = new Set(signatories.map((row) => nameKey(row.name)).filter(Boolean));
  for (const person of reading.officers || []) {
    const key = nameKey(person.name);
    if (!key || listed.has(key)) continue;
    listed.add(key);
    const details = {
      name: clean(person.name),
      capacity: signatoryCapacity(person.roles),
      nationality: clean(person.nationality),
      dateOfBirth: clean(person.dateOfBirth),
      signatureMode: person.signingPower === "joint" ? "joint" : person.signingPower === "sole" ? "sole" : "",
    };
    const emptyIndex = signatories.findIndex((row) => !clean(row.name));
    const row = emptyIndex >= 0
      ? { ...signatories[emptyIndex], ...details }
      : { _rowId: rowId("sig"), countryOfResidence: "", jointWithSignatoryIds: [], ...details };
    if (!clean(row.countryOfResidence) && clean(person.countryOfResidence)) row.countryOfResidence = clean(person.countryOfResidence);
    if (!row._rowId) row._rowId = rowId("sig");
    if (emptyIndex >= 0) signatories[emptyIndex] = row; else signatories.push(row);
    prefill.signatories[row._rowId] = row.name;
  }
  ownership.signatories = signatories;

  // Shareholders: only into an empty ownership, and only individuals (no company among the shareholders).
  const named = (list) => (Array.isArray(list) ? list : []).some((row) => clean(row?.name));
  const ownershipEmpty = !named(ownership.shareholders) && !named(ownership.directShareholders) &&
    !named(ownership.mobileAssistantDraft?.roots) && !ownership.assistantGenerated &&
    !clean(ownership.desktopClientStructureType) && !clean(ownership.desktopClientCompletionMethod);
  const shareholders = (reading.shareholders || []).filter((person) => clean(person.name));
  const companies = reading.corporateShareholders ? (reading.companyShareholders || []).filter((company) => clean(company.name)) : [];
  const assistantNode = (person, type, percent) => ({
    id: rowId("node"), type, entityCategory: "", holdingKind: "",
    name: clean(person.name),
    nationality: type === "individual" ? clean(person.nationality) : "",
    // A company's country of incorporation is kept in the assistant as countryOfResidence.
    countryOfResidence: clean(type === "individual" ? person.countryOfResidence : person.country),
    ownershipPercent: assistantPercent(percent),
    ownersCount: "", owners: [], trustParties: null, foundationParties: null,
  });
  if (!mobile && companies.length && ownershipEmpty) {
    const direct = [...companies, ...shareholders];
    const percents = completedPercents(direct);
    ownership.documentShareholders = direct.map((person, index) => (index < companies.length
      ? { kind: "company", name: clean(person.name), country: clean(person.country), percent: percents[index] }
      : { kind: "individual", name: clean(person.name), nationality: clean(person.nationality),
        countryOfResidence: clean(person.countryOfResidence), percent: percents[index] }));
  } else if (mobile && companies.length && ownershipEmpty) {
    const direct = [...companies, ...shareholders];
    const percents = completedPercents(direct);
    const roots = direct.map((person, index) => assistantNode(person, index < companies.length ? "entity" : "individual", percents[index]));
    // documentsCompanyShareholders: the chat starts by explaining why the owners of these companies are asked.
    ownership.mobileAssistantDraft = { preliminaryMode: "", directCount: String(roots.length), roots,
      documentsCompanyShareholders: companies.map((company) => clean(company.name)) };
    ownership.documentPrefillOwnership = "chat";
  } else if (!reading.corporateShareholders && shareholders.length && ownershipEmpty) {
    const percents = completedPercents(shareholders);
    if (mobile) {
      const roots = shareholders.map((person, index) => assistantNode(person, "individual", percents[index]));
      // The organisation type is added by the Ownership step when it generates the result.
      ownership.mobileAssistantDraft = { preliminaryMode: "", directCount: String(roots.length), roots };
      ownership.documentPrefillOwnership = "generate";
    } else {
      const rows = shareholders.map((person, index) => ({
        _rowId: rowId("sh"),
        name: clean(person.name),
        holderType: "Individual",
        beneficialOwnerType: "Beneficial owner",
        nationality: clean(person.nationality),
        countryOfResidence: clean(person.countryOfResidence),
        ownershipPercent: percents[index] === null ? "" : String(percents[index]),
      }));
      ownership.desktopClientStructureType = "direct";
      ownership.desktopClientCompletionMethod = "manual";
      ownership.shareholders = rows;
      ownership.directShareholders = rows;
      rows.forEach((row) => { prefill.shareholders[row._rowId] = row.name; });
    }
  }

  // Mobile: done again once the Ownership step has generated the shareholders.
  const linked = linkPrefilledSignatories(ownership, prefill.signatories);

  customer.documentPrefill = prefill;
  return { ...form, customer, ownership: linked };
}

// True while a field still holds the value filled from the documents (the "From your documents" mark).
export function isPrefilledCustomerField(customer, field) {
  const value = customer?.documentPrefill?.[field];
  if (!value) return false;
  return field === "dobOrIncorporationDate" ? isoDate(customer?.[field]) === value : clean(customer?.[field]) === value;
}
export function isPrefilledRow(customer, kind, row) {
  const value = customer?.documentPrefill?.[kind]?.[row?._rowId];
  return Boolean(value) && clean(row?.name) === value;
}
